import * as React from 'react'
import { Text, View, ScrollView, FlatList, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Drawer from '@components/drawer/Drawer'
import TextInput from '@components/textInput/TextInput'
import Icon from '@components/icon/Icons'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import SimpleAlert from '@components/modal/SimpleAlert'

import EDLListHeaderMobile from './filters/EDLListHeaderMobile'
import GroupedFilters from './filters/GroupedFilters'
import InlineFilters from './filters/InlineFilters'

import ManifestationCard from './cards/ManifestationCard'
import FormCard from './cards/FormCard'
import EDLCard from './cards/EDLCard'
import EDLGroupedCard from './cards/EDLGroupedCard'

import api from '@api/api'
import logger from '@utils/logger'
import { orderTenants } from '@screens/tenants/tenantsUtils'
import { orderManifestations } from '@screens/manifestations/manifestationsUtils'

import { AuthScope, EtatDesLieuxTypeEnum } from '@api/enum'
import { OPEN_INCIDENT_STATES } from '@utils/useSiteChangeHandler'

import { createUUID } from '@utils/uuid'
import { format, getMonth, getYear, startOfYear, addMonths } from 'date-fns'

import useEDL, { isManifestationIN, isManifestationOUT } from './useEDL'
import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as EDLStore from '@store/edl/edl'
import * as IncidentStore from '@store/incident/incident'
import * as LocationStore from '@store/location/location'
import * as FamilleStore from '@store/famille/famille'
import * as SocieteStore from '@store/societe/societe'
import * as AuthStore from '@store/auth/auth'

import config from '@app/config'
import exporter from '@utils/exporter'
import Logger from '@utils/logger'
import Loader from '@components/loader/Loader'
import Modal from '@components/modal/Modal'

type Tab = 'inProgress' | 'toDo' | 'finished'

interface Props {
  manifestationId?: string
  list?: Tab
}

const EDLList = ({ manifestationId, list }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const [filters, setFilters] = React.useState<EDLFilter[]>([])
  const [search, setSearch] = React.useState('')
  const [tab, setTab] = React.useState<Tab>(list || 'inProgress')
  const [grouped, setGrouped] = React.useState(manifestationId)
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)
  const [isHoverFilter, setIsHoverFilter] = React.useState(false)
  const [exportingPDF, setExportingPDF] = React.useState(false)
  const [exportingXLSX, setExportingXLSX] = React.useState(false)

  const [manifestations, tenants, EDLs, forms, allManifestations, allTenants, allEDLs] = useEDL(filters, search, tab)

  const years = React.useMemo(
    () => Array.from(new Set(EDLs.map((m) => getYear(new Date(m.dateDeValidation))))).reverse(),
    [EDLs]
  )
  const [year, setYear] = React.useState(years[0])
  const [month, setMonth] = React.useState<number>()

  const canCreateEntryEDL = scopes.includes(AuthScope.EdlEntreeCreation)
  const canCreateExitEDL = scopes.includes(AuthScope.EdlSortieCreation)

  const yearEDLs = React.useMemo(
    () => allEDLs.filter((edl) => getYear(new Date(edl.dateDeValidation)) === year),
    [year, allEDLs.length]
  )

  const TABS =
    scopes.includes(AuthScope.EdlEntreeCreation) || scopes.includes(AuthScope.EdlSortieCreation)
      ? ['inProgress', 'toDo', 'finished']
      : ['finished']

  const onScreenClick = () => {
    if (!isHoverFilter) {
      setIsFilterOpen(false)
    }
  }

  React.useEffect(() => {
    setYear(years[0])
  }, [years])

  React.useEffect(() => {
    if (manifestationId) {
      const manif = allManifestations.find((m) => m.id === manifestationId)

      if (manif) {
        setSearch(manif.libelle)
      }
    }
  }, [allManifestations.length, manifestationId])

  React.useEffect(() => {
    // background only
    Promise.all([
      api.edl.all(site.id),
      api.locatairePermanent.all(site.id),
      api.manifestation.all(site.id),
      api.localisation.all(site.id, true),
      api.incident.search({ etatsIncident: OPEN_INCIDENT_STATES, siteId: site.id }),
      api.familles.all(site.id, true),
      api.societe.all(site.id),
    ])
      .then((res) => {
        EDLStore.actions.setFinishedEDL(res[0])
        SiteStore.actions.setTenants(orderTenants(res[1]))
        SiteStore.actions.setManifestations(orderManifestations(res[2]))
        LocationStore.actions.setPlaces(res[3])
        IncidentStore.actions.setIncidents(res[4])
        FamilleStore.actions.setActivities(res[5])
        SocieteStore.actions.setServices(res[6])
      })
      .catch(logger.error)
  }, [])

  React.useEffect(() => {
    if (isFilterOpen) {
      window.addEventListener('click', onScreenClick)
      return () => window.removeEventListener('click', onScreenClick)
    }
  }, [isFilterOpen, isHoverFilter])

  React.useEffect(() => {
    if (exportingPDF) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({
            Content: () => <Loader />,
            position: 'center',
          })
        }
        exporter
          .download<EtatDesLieuxPDFRequest>(config.ws.edl.downloadPDF, `export_edl.pdf`, 'application/pdf', {
            etatDesLieuxIds: EDLs.map((edl) => edl.id),
          })
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.edlList.export.success.title'),
                description: i18n.t('screens.edlList.export.success.description'),
                buttons: [{ label: i18n.t('screens.edlList.export.success.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((error) => {
            Logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.edlList.export.error.title'),
              description: i18n.t('screens.edlList.export.error.description'),
              buttons: [{ label: i18n.t('screens.edlList.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExportingPDF(false)
          })
      }
    }
  }, [exportingPDF])

  React.useEffect(() => {
    if (exportingXLSX) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({
            Content: () => <Loader />,
            position: 'center',
          })
        }
        exporter
          .download<EtatDesLieuxExportRequest>(
            config.ws.edl.downloadXLSX,
            `export_edl.xlsx`,
            'application/vnd.ms-excel',
            { etatDesLieuxIds: EDLs.map((edl) => edl.id) }
          )
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.edlList.export.success.title'),
                description: i18n.t('screens.edlList.export.success.description'),
                buttons: [{ label: i18n.t('screens.edlList.export.success.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((error) => {
            Logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.edlList.export.error.title'),
              description: i18n.t('screens.edlList.export.error.description'),
              buttons: [{ label: i18n.t('screens.edlList.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExportingXLSX(false)
          })
      }
    }
  }, [exportingXLSX])

  const exportEDL = () => {
    SimpleAlert.open({
      title: i18n.t('screens.edlRecap.popup.title'),
      description: i18n.t('screens.edlRecap.popup.description'),
      buttons: [
        {
          label: i18n.t('screens.edlRecap.popup.pdf'),
          onClick: () => {
            SimpleAlert.close()
            setExportingPDF(true)
          },
          disabled: exportingPDF,
        },
        {
          label: i18n.t('screens.edlRecap.popup.xls'),
          onClick: () => {
            SimpleAlert.close()
            setExportingXLSX(true)
          },
          disabled: exportingXLSX,
        },
      ],
    })
  }

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t('screens.edlList.title'), onClick: () => navigation.push('/edl') },
  ]

  const headerButtons: HeaderButton[] =
    tab === 'finished'
      ? [
          {
            leftIcon: 'download',
            label: i18n.t('screens.edlList.download'),
            onClick: exportEDL,
            color: 'default',
          },
        ]
      : []

  const onBack = () => {
    if (grouped) {
      setGrouped(undefined)
    } else {
      navigation.goBack()
    }
  }

  const onStart = (type: EtatDesLieuxTypeEnum, id?: string, manif?: ManifestationResponse, tenant?: Tenant) => {
    const eventId = id || createUUID()

    navigation.push(`/edl/spaces/${eventId}?type=${type}&origin=edl`, {
      manifestationId: manif?.id,
      tenantId: tenant?.tenant.id,
    })
  }

  const onConsult = (id: string, manif?: ManifestationResponse, tenant?: Tenant) => {
    navigation.push(`/edl/detail/${id}`, { manifestationId: manif?.id, tenantId: tenant?.tenant.id })
  }

  const openFilterMobile = () => {
    Drawer.open({
      render: () => (
        <GroupedFilters
          onClose={() => Drawer.close()}
          onSave={setFilters}
          items={filters}
          onReset={() => setFilters([])}
        />
      ),
      hideTarget: true,
    })
  }

  const renderYear = ({ item }: { item: number }) => {
    const selected = year === item

    return (
      <YearContainer isSelected={selected} onPress={() => setYear(item)}>
        <Year isSelected={selected}>{item}</Year>
      </YearContainer>
    )
  }

  const renderFinishedItem = (item: EtatDesLieuxResponse, idx: number) => {
    const manif = allManifestations.find((m) => m.id === item.manifestationId)
    const tenant = allTenants.find((t) => t.tenant.id === item.locatairePermanentId)

    if (!manif && !tenant) {
      return null
    }

    return (
      <EventContainer key={`finished_${item.id}_${idx}`}>
        <EDLCard
          edl={item}
          manifestation={manif}
          tenant={tenant}
          onClick={() => onConsult(item.id, manif, tenant)}
          places={places}
        />
      </EventContainer>
    )
  }

  const renderFinishedGroupedItem = (
    item: EtatDesLieuxResponse[],
    month: number,
    manifestationId?: string,
    tenantId?: string
  ) => {
    const manif = allManifestations.find((m) => m.id === manifestationId)
    const tenant = allTenants.find((t) => t.tenant.id === tenantId)

    const items = yearEDLs
      .filter((it) => getMonth(new Date(it.dateDeValidation)) === month)
      .filter((it) => it.manifestationId === manifestationId || it.locatairePermanentId === tenantId)

    if (!manif && !tenant) {
      return null
    }

    return (
      <EventContainer key={`finished_group_${manifestationId || tenantId}`}>
        <EDLGroupedCard
          group={items}
          manifestation={manif}
          tenant={tenant}
          onClick={() => {
            setGrouped(manifestationId || tenantId)
            setMonth(month)
          }}
        />
      </EventContainer>
    )
  }

  const renderMonthGroup = (month: number, items: EtatDesLieuxResponse[]) => {
    const manifIds = Array.from(new Set(items.map((m) => m.manifestationId).filter(Boolean)))
    const tenantIds = Array.from(new Set(items.map((m) => m.locatairePermanentId).filter(Boolean)))

    const groupMonth = format(addMonths(startOfYear(Date.now()), month), 'MMMM', { locale: i18n.locales[i18n.lang] })

    return (
      <React.Fragment key={month}>
        <Month>{`${groupMonth} ${year}`}</Month>
        <List>
          {manifIds.map((m) =>
            renderFinishedGroupedItem(
              items.filter((it) => it.manifestationId === m),
              month,
              m
            )
          )}
          {tenantIds.map((t) =>
            renderFinishedGroupedItem(
              items.filter((it) => it.locatairePermanentId === t),
              month,
              undefined,
              t
            )
          )}
        </List>
      </React.Fragment>
    )
  }

  const renderFinishedGroup = (items: EtatDesLieuxResponse[]) => {
    const months = Array.from(new Set(items.map((m) => getMonth(new Date(m.dateDeValidation)))))

    if (items.length === 0) {
      return <MissingInfo>{i18n.t('screens.manifestations.noManifestation')}</MissingInfo>
    }

    return (
      <>
        {months.map((m) =>
          renderMonthGroup(
            m,
            items.filter((it) => getMonth(new Date(it.dateDeValidation)) === m)
          )
        )}
      </>
    )
  }

  const renderInProgressItem = (item: EDLForm, idx: number) => {
    const manif = manifestations.find((m) => m.id === item.manifestationId)
    const tenant = tenants.find((t) => t.tenant.id === item.locatairePermanentId)

    if (!manif && !tenant) {
      return null
    }

    return (
      <EventContainer key={`progress_${item.id}_${idx}`}>
        <FormCard
          form={item}
          manifestation={manif}
          tenant={tenant}
          onClick={() => onStart(item.type, item.id, manif, tenant)}
          places={places}
        />
      </EventContainer>
    )
  }

  const renderTodoItem = (item: ManifestationEDLListResponse, idx: number) => {
    const isIN = isManifestationIN(item) && !item.isOut
    const manif = manifestations.find((m) => m.id === item.id)

    return (
      <EventContainer key={`todo_${item.id}_${idx}_${isIN ? 'entree' : 'sortie'}`}>
        <ManifestationCard
          manifestation={item}
          onClick={() => onStart(isIN ? EtatDesLieuxTypeEnum.ENTREE : EtatDesLieuxTypeEnum.SORTIE, undefined, manif)}
          places={places}
          isIN={isIN}
        />
      </EventContainer>
    )
  }

  const renderHeader = () => {
    return (
      <>
        <SearchContainer>
          <TextInput
            onChange={setSearch}
            placeholder={i18n.t('screens.incidentList.search')}
            left={{ name: 'search' }}
            value={search}
          />
        </SearchContainer>
        <InlineFilters
          onClose={() => Drawer.close()}
          onSave={setFilters}
          items={filters}
          onReset={() => setFilters([])}
          setIsFilterOpen={() => setIsFilterOpen(!isFilterOpen)}
          isFilterOpen={isFilterOpen}
          onHover={setIsHoverFilter}
        />
      </>
    )
  }

  const tabs = TABS.map((t) => ({ label: i18n.t(`screens.edlList.${t}`), value: t }))
  const selectedTab = tabs.find((t) => t.value === tab)

  const displayedManif = manifestations.reduce((acc, cur) => {
    if (
      cur.actif &&
      ((isManifestationIN(cur) && !cur.isOut && canCreateEntryEDL) || (isManifestationOUT(cur) && canCreateExitEDL))
    ) {
      acc.push(cur)
    }
    return acc
  }, [] as ManifestationResponse[])

  const renderContent = () => {
    return (
      <MainContainer>
        <SegmentedContainer>
          <SegmentedControl data={tabs} value={selectedTab} onItemSelected={(item) => setTab(item.value)} />
        </SegmentedContainer>
        {tab === 'toDo' ? (
          <ScrollView>
            {displayedManif.length === 0 ? (
              <MissingInfo>{i18n.t('screens.edlList.tab.empty.toDo')}</MissingInfo>
            ) : (
              <List key="toDo">{displayedManif.map(renderTodoItem)}</List>
            )}
          </ScrollView>
        ) : tab === 'inProgress' ? (
          <ScrollView>
            {forms.length === 0 ? (
              <MissingInfo>{i18n.t('screens.edlList.tab.empty.inProgress')}</MissingInfo>
            ) : (
              <List key="inProgress">{forms.map(renderInProgressItem)}</List>
            )}
          </ScrollView>
        ) : EDLs.length === 0 ? (
          <MissingInfo>{i18n.t('screens.edlList.tab.empty.finished')}</MissingInfo>
        ) : grouped && month !== undefined ? (
          <ScrollView>
            <BackContainer
              onPress={() => {
                setGrouped(undefined)
                setMonth(undefined)
              }}>
              <IconContainer>
                <Icon name="arrow-left" size={20} color={theme.colors.primary} />
              </IconContainer>
              <BackText>{i18n.t('screens.edlList.edlListBack')}</BackText>
            </BackContainer>
            <List key="finished">
              {EDLs.filter((edl) => edl.manifestationId === grouped || edl.locatairePermanentId === grouped)
                .filter((edl) => getYear(new Date(edl.dateDeValidation)) === year)
                .filter((annualEdl) => getMonth(new Date(annualEdl.dateDeValidation)) === month)
                .map(renderFinishedItem)}
            </List>
          </ScrollView>
        ) : (
          <ScrollView>
            {years.length > 0 && (
              <TabContainer>
                <FlatList data={years} renderItem={renderYear} horizontal keyExtractor={(item) => `${item}`} />
              </TabContainer>
            )}
            {renderFinishedGroup(EDLs.filter((edl) => getYear(new Date(edl.dateDeValidation)) === year))}
          </ScrollView>
        )}
      </MainContainer>
    )
  }

  if (isPhone) {
    return (
      <Root
        onLeft={navigation.goBack}
        header={i18n.t('screens.edlList.title')}
        fixed
        renderHeader={() => (
          <EDLListHeaderMobile
            filtersSize={filters.length}
            onBack={onBack}
            onExport={exportEDL}
            onFilter={openFilterMobile}
            onSearch={setSearch}
            search={search}
            title={i18n.t('screens.edlList.title')}
            canDownload={headerButtons.length > 0}
          />
        )}>
        {renderContent()}
      </Root>
    )
  }

  return (
    <Root onLeft={navigation.goBack} header={i18n.t('screens.edlList.title')} fixed>
      <Header
        title={i18n.t('screens.edlList.title')}
        links={headerLinks}
        buttons={headerButtons}
        renderBottom={renderHeader}
      />
      {renderContent()}
    </Root>
  )
}

export default EDLList

const MainContainer = styled(View)`
  flex: 1;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px` : '24px')};
`

const SearchContainer = styled(View)`
  margin: 24px 0;
`

const SegmentedContainer = styled(View)`
  margin-bottom: 16px;
`

const List = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`};
`

const EventContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 8px;' : 'margin: 0px 16px 16px 0px;')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-direction: row;'};
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
  margin: 16px 0;
`

const TabContainer = styled(View)`
  padding-bottom: 26px;
`

const YearContainer = styled(TouchableOpacity)<{ isSelected: boolean }>`
  ${(props) => props.isSelected && `border: 0px solid ${props.theme.colors.primary};`}
  ${(props) => props.isSelected && `border-bottom-width: 2px;`}
  margin-right: 12px;
  padding-bottom: 6px;
`

const Year = styled(Text)<{ isSelected: boolean }>`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => (props.isSelected ? props.theme.colors.primary : props.theme.colors.disable)};
`

const BackContainer = styled(TouchableOpacity)`
  flex-direction: row;
  padding: 32px 32px 32px 12px;
  align-items: center;
`

const IconContainer = styled(View)`
  padding-right: 16px;
`

const BackText = styled(Text)`
  ${(props) => props.theme.fonts.Option};
`

const Month = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  text-transform: capitalize;
  margin-bottom: 15px;
  margin-top: 10px;
`
